import React from 'react';
import { motion } from 'framer-motion';
import { SunriseIcon, SunIcon, SunsetIcon, MoonIcon } from 'lucide-react';
type TimelineStep = {
  time: string;
  label: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
};
export function MechanismSection() {
  const steps: TimelineStep[] = [{
    time: '6–8 AM',
    label: 'Step 1',
    title: 'The Cortisol Peak',
    description: 'Your stress hormone is naturally highest. What you eat NOW determines your fat-storage signal for the entire day.',
    icon: <SunriseIcon className="w-6 h-6" aria-hidden="true" data-id="element-255" />,
    color: 'bg-terracotta/10 text-terracotta border-terracotta/30'
  }, {
    time: '12–2 PM',
    label: 'Step 2',
    title: 'The Thermal Window Opens',
    description: "Your body's anti-inflammatory window. The right foods here begin the cortisol reset.",
    icon: <SunIcon className="w-6 h-6" aria-hidden="true" data-id="element-256" />,
    color: 'bg-sage/20 text-forest border-sage/40'
  }, {
    time: '5–7 PM',
    label: 'Step 3',
    title: 'The Adrenal Switch',
    description: "Signal your nervous system that you're safe. This is when you turn OFF fat-storage mode.",
    icon: <SunsetIcon className="w-6 h-6" aria-hidden="true" data-id="element-257" />,
    color: 'bg-forest/10 text-forest border-forest/20'
  }, {
    time: '8–10 PM',
    label: 'Step 4',
    title: 'The Sleep-to-Slim Connection',
    description: "What you eat (or don't) in this window determines whether you wake at 3am — or sleep through to morning.",
    icon: <MoonIcon className="w-6 h-6" aria-hidden="true" data-id="element-258" />,
    color: 'bg-terracotta/10 text-terracotta border-terracotta/30'
  }];
  return <section className="py-16 md:py-24" style={{
    background: 'linear-gradient(135deg, #F0F5EF 0%, #FAF8F5 50%, #F5F0EB 100%)'
  }} aria-labelledby="mechanism-heading" data-id="element-259">
      <div className="max-w-5xl mx-auto px-4" data-id="element-260">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-261">
          <p className="font-sans text-terracotta text-xs font-700 tracking-[0.2em] uppercase mb-4" data-id="element-262">
            The Discovery
          </p>
          <h2 id="mechanism-heading" className="font-serif text-forest text-3xl md:text-4xl font-700 italic leading-tight mb-5" data-id="element-263">
            Introducing the Thermal Window Protocol
          </h2>
          <p className="font-sans text-stone-600 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-264">
            The specific times of day when your body is biologically primed to
            drop inflammation — and what to eat during them.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative" data-id="element-265">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-16 left-0 right-0 h-0.5 bg-sage/30 z-0" aria-hidden="true" data-id="element-266" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10" data-id="element-267">
            {steps.map((step, index) => <motion.div key={step.label} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.12
          }} className="flex flex-col items-center text-center" data-id="element-268">
                {/* Icon circle */}
                <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center mb-4 bg-white shadow-md ${step.color}`} data-id="element-269">
                  {step.icon}
                </div>

                {/* Time badge */}
                <span className="font-sans text-xs font-700 tracking-wider text-terracotta uppercase mb-1" data-id="element-270">
                  {step.time}
                </span>

                {/* Card */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-sage/20 w-full" data-id="element-271">
                  <h3 className="font-serif text-forest text-lg font-700 mb-2" data-id="element-272">
                    {step.title}
                  </h3>
                  <p className="font-sans text-stone-600 text-sm leading-relaxed" data-id="element-273">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Closing line */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="font-serif text-forest text-xl md:text-2xl italic text-center mt-14 max-w-3xl mx-auto leading-relaxed" data-id="element-274">
          "This isn't about eating less. It's about eating at the right time,
          with the right foods, to send a different signal to your brain."
        </motion.p>
      </div>
    </section>;
}